/**
 * SearchResultItem Component
 *
 * Individual search result item with title, excerpt, path breadcrumb and match type badge.
 * Renders highlighted excerpt with <mark> tags from FTS5 search.
 *
 * Story 2-4: SearchResults Component
 *
 * @security excerpt contains <mark> tags from backend FTS5 highlighting.
 * Backend (manualSearchService) sanitizes content before adding marks.
 * XSS risk is mitigated by server-side sanitization.
 */

import type { SearchResult } from '@functieloonhuis/shared';

interface SearchResultItemProps {
  result: SearchResult;
  isSelected: boolean;
  onSelect: () => void;
  index: number;
}

/** Maximum title length before truncation */
const MAX_TITLE_LENGTH = 60;

/**
 * Match type badge colors and labels (Dutch)
 */
const matchTypeBadges: Record<SearchResult['matchType'], { label: string; className: string }> = {
  title: { label: 'Titel', className: 'bg-blue-100 text-blue-800' },
  keyword: { label: 'Trefwoord', className: 'bg-green-100 text-green-800' },
  content: { label: 'Inhoud', className: 'bg-gray-100 text-gray-700' },
};

/**
 * Truncate text to max length with ellipsis
 */
function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '...';
}

/**
 * Format path array as breadcrumb string
 * Truncates if path has more than 3 items
 */
function formatPath(path: string[]): string {
  if (path.length === 0) return '';
  if (path.length <= 3) return path.join(' › ');
  // Truncate middle for long paths: "DEEL 1 › ... › Taken"
  return `${path[0]} › ... › ${path[path.length - 1]}`;
}

/** Default badge for fallback (TypeScript noUncheckedIndexedAccess safety) */
const defaultBadge = { label: 'Inhoud', className: 'bg-gray-100 text-gray-700' } as const;

export function SearchResultItem({ result, isSelected, onSelect, index }: SearchResultItemProps) {
  const badge = matchTypeBadges[result.matchType] ?? defaultBadge;

  return (
    <li
      role="option"
      aria-selected={isSelected}
      data-testid={`search-result-${index}`}
      id={`search-result-option-${index}`}
    >
      <button
        type="button"
        onClick={onSelect}
        className={`w-full px-4 py-3 text-left transition-colors focus:outline-none ${
          isSelected
            ? 'bg-blue-50 ring-2 ring-inset ring-blue-500'
            : 'hover:bg-gray-50 focus:bg-gray-50'
        }`}
        tabIndex={-1} // Navigation handled by parent
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {/* Breadcrumb path */}
            {result.path.length > 0 && (
              <div className="text-xs text-gray-400 mb-1 truncate" aria-label="Locatie">
                {formatPath(result.path)}
              </div>
            )}

            {/* Title - truncate if exceeds max length */}
            <div className="font-medium text-gray-900 truncate" title={result.title}>
              {truncateText(result.title, MAX_TITLE_LENGTH)}
            </div>

            {/* Excerpt with highlighted matches */}
            {result.excerpt && (
              <div
                className="text-sm text-gray-600 mt-1 line-clamp-2 [&_mark]:bg-yellow-200 [&_mark]:px-0.5 [&_mark]:rounded-sm"
                dangerouslySetInnerHTML={{ __html: result.excerpt }}
              />
            )}
          </div>

          {/* Match type badge */}
          <span
            className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full ${badge.className}`}
            aria-label={`Gevonden in: ${badge.label.toLowerCase()}`}
          >
            {badge.label}
          </span>
        </div>
      </button>
    </li>
  );
}
